import { Archive, CircleCheck, CircleX } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { RetentionSettings, RetentionState } from '@orgus/protocol';
import { Button, ConfirmDialog, Dialog, ErrorState, RelativeTime, Select, SkeletonRows, formatBytes, formatDate, formatInt, plural, toUiError, toast } from '@orgus/ui';
import { liveDeletedBy, retentionProblem } from './model.ts';
import { archiveBatch, getRetention, runRetention, saveRetention } from './useAuditQuery.ts';

const DAY = 86_400_000;
const LIVE_OPTIONS = [7, 30, 90, 180, 365];
const ARCHIVE_OPTIONS = [365, 730, 1825, 3650];
const daysLabel = (d: number) => (d % 365 === 0 ? plural(d / 365, 'year') : plural(d, 'day'));
const toValue = (d: number | null) => (d == null ? 'never' : String(d));
const fromValue = (v: string) => (v === 'never' ? null : Number(v));

/** Segments a new archive age would delete at the next run. */
export function prunedBy(state: RetentionState, archiveDays: number | null, now: number): { segments: number; entries: number; oldest: number | null } {
  if (archiveDays == null) return { segments: 0, entries: 0, oldest: null };
  const hit = state.archive.segments.filter((s) => !s.pruned && s.to < now - archiveDays * DAY);
  return { segments: hit.length, entries: hit.reduce((n, s) => n + s.count, 0), oldest: hit.length ? Math.min(...hit.map((s) => s.from)) : null };
}

/** Retention: how long entries stay in the live log, how long archived segments are kept, and a run now. */
export function RetentionDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [state, setState] = useState<RetentionState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [draft, setDraft] = useState<RetentionSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  // A save or run that failed, said inside the dialog: errors never go in a toast.
  const [actionError, setActionError] = useState<unknown>(null);
  const [confirm, setConfirm] = useState(false);
  /** What the next run would archive with the draft's live age (null while unknown). */
  const [batch, setBatch] = useState<{ total: number; newest: number | null } | null>(null);

  const load = async () => {
    setError(null);
    try {
      const s = await getRetention();
      setState(s);
      setDraft(s.settings);
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    if (open) void load();
  }, [open]);
  const draftLive = draft?.liveDays;
  useEffect(() => {
    if (!open || draftLive === undefined) return;
    let live = true;
    setBatch(null);
    archiveBatch(draftLive)
      .then((b) => live && setBatch(b))
      .catch(() => live && setBatch(null));
    return () => {
      live = false;
    };
  }, [open, draftLive]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setActionError(null);
    try {
      const s = await saveRetention(draft);
      setState(s);
      setDraft(s.settings);
      toast('Retention saved', { tone: 'success', description: 'It applies from the next run.' });
    } catch (e) {
      setActionError(toUiError(e, { what: 'Retention was not saved.' }));
    } finally {
      setSaving(false);
    }
  };

  const now = Date.now();
  const willPrune = state && draft ? prunedBy(state, draft.archiveDays, now) : null;
  /** Live entries the next run would archive and delete in the same run. */
  const liveLost = draft ? liveDeletedBy(batch, draft, now) : 0;
  const problem = draft ? retentionProblem(draft) : null;
  const changed = !!state && !!draft && (state.settings.liveDays !== draft.liveDays || state.settings.archiveDays !== draft.archiveDays);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Audit retention"
      description="Old entries move to a compressed archive that Verify chain and search still cover."
      footer={
        <>
          <Button
            variant="outline"
            icon={<Archive />}
            loading={running}
            disabled={!state || changed}
            title={changed ? 'Save first, then archive' : undefined}
            onClick={async () => {
              setRunning(true);
              setActionError(null);
              try {
                const r = await runRetention();
                setState(r.state);
                if (r.run.ok) toast('Retention ran', { tone: 'success', description: r.run.message });
                else setActionError({ what: 'Retention stopped before it finished.', why: r.run.message || 'The hub did not say why.', next: 'Run it again. If it stops again, the hub log says more.' });
              } catch (e) {
                setActionError(toUiError(e, { what: 'Retention did not run.' }));
              } finally {
                setRunning(false);
              }
            }}
          >
            Archive now
          </Button>
          <Button variant="primary" loading={saving} disabled={!changed || problem != null} onClick={() => ((willPrune && willPrune.segments) || liveLost ? setConfirm(true) : void save())}>
            Save
          </Button>
        </>
      }
    >
      {error != null && !state ? (
        <ErrorState variant="inline" error={error} onRetry={load} />
      ) : !state || !draft ? (
        <div role="status" aria-busy="true" aria-label="Loading retention">
          <SkeletonRows rows={4} />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {actionError != null && <ErrorState variant="inline" error={actionError} />}
          <dl className="grid grid-cols-1 gap-3 rounded-md border border-border-subtle bg-bg px-4 py-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-fg-subtle">Live log</dt>
              <dd className="text-fg">
                {formatInt(state.live.entries)} entries, {formatBytes(state.live.bytes)}
                {state.live.oldest != null && <span className="text-fg-muted">, oldest from {formatDate(state.live.oldest)}</span>}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-fg-subtle">Archive</dt>
              <dd className="text-fg">
                {state.archive.segments.length === 0 ? (
                  <span className="text-fg-muted">Nothing archived yet</span>
                ) : (
                  <>
                    {formatInt(state.archive.entries)} entries in {plural(state.archive.segments.filter((s) => !s.pruned).length, 'segment')}
                    {state.archive.pruned > 0 && <span className="text-fg-muted">, {formatInt(state.archive.pruned)} pruned (hashes kept)</span>}
                  </>
                )}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-fg-subtle">Last run</dt>
              <dd className="flex items-start gap-1.5 text-fg-muted">
                {state.lastRun ? (
                  <>
                    {state.lastRun.ok ? <CircleCheck className="mt-0.5 size-3.5 shrink-0 text-passed" aria-hidden /> : <CircleX className="mt-0.5 size-3.5 shrink-0 text-failed" aria-hidden />}
                    <span>
                      <span className="sr-only">{state.lastRun.ok ? 'Succeeded. ' : 'Stopped. '}</span>
                      <RelativeTime ts={state.lastRun.at} /> by {state.lastRun.by}: {state.lastRun.message}
                    </span>
                  </>
                ) : (
                  'Not run yet. It runs a minute after the hub starts, then every 6 hours.'
                )}
              </dd>
            </div>
          </dl>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Keep in the live log"
              hint="Older entries move to the archive."
              value={toValue(draft.liveDays)}
              onValueChange={(v) => setDraft({ ...draft, liveDays: fromValue(v) })}
              options={[...LIVE_OPTIONS.map((d) => ({ value: String(d), label: daysLabel(d) })), { value: 'never', label: 'Forever (never archive)' }]}
            />
            <Select
              label="Delete archived segments after"
              hint="Their hashes stay, so the chain still verifies."
              error={problem ?? undefined}
              value={toValue(draft.archiveDays)}
              onValueChange={(v) => setDraft({ ...draft, archiveDays: fromValue(v) })}
              options={[{ value: 'never', label: 'Never (keep forever)' }, ...ARCHIVE_OPTIONS.map((d) => ({ value: String(d), label: daysLabel(d) }))]}
            />
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Delete archived audit entries older than ${draft?.archiveDays != null ? daysLabel(draft.archiveDays) : ''}?`}
        description={[
          willPrune && willPrune.segments
            ? `At the next run ${plural(willPrune.segments, 'archived segment')} holding ${plural(willPrune.entries, 'entry', 'entries')}${willPrune.oldest ? `, the oldest from ${formatDate(willPrune.oldest)},` : ''} will be deleted for good.`
            : '',
          liveLost ? `${plural(liveLost, 'entry', 'entries')} now in the live log ${liveLost === 1 ? 'is' : 'are'} older than both ages: the next run archives and deletes ${liveLost === 1 ? 'it' : 'them'} in one go.` : '',
          'Their hashes stay in the archive manifest, so the rest of the chain still verifies. Export them first if you need to keep them.',
        ]
          .filter(Boolean)
          .join(' ')}
        confirmLabel="Save and delete at next run"
        onConfirm={save}
      />
    </Dialog>
  );
}
